import {
  Paper,
  Button,
  Container,
  TextField,
  Grid,
  Typography
} from '@material-ui/core';
import React, { useEffect, useRef, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import SwitchInput from '../components/SwitchInput';
import DescriptionInput from '../components/DescriptionInput.jsx';
import HoursInput from '../components/HoursInput';
import dateUtil from '../util/dateUtil';
import { useDebouncedEffect } from '../redux/hooks/use-debounce';
import ApiMessage from '../components/ApiMessage.jsx';
import DateInput from '../components/DateInput.jsx';
import TimeInput from '../components/TimeInput.jsx';
import ReactCardFlip from 'react-card-flip';
import { Create } from '@material-ui/icons';
import TaskInput from '../components/TaskInput.jsx';
import ConfirmDialog from '../components/ConfirmDialog.jsx';

const useStyles = makeStyles({
  buttonContainer: {
    textAlign: 'center',
  },
  timers: {
    whiteSpace: 'nowrap',
    fontSize: 14,
  },
  actionBar: {},
});

const EntryForm = ({
  entry,
  onCancel,
  onSave,
  onAutoSave,
  disabled,
  closeLabel = 'Close',
  cancelLabel = 'Cancel',
  saveLabel = 'Save',
  onValidate,
  smallDates = false,
  focus = 'description',
  defaultDirty = false,
  apiLabel,
  onClearMessage,
}) => {
  const classes = useStyles();
  //local state
  const [errors, setErrors] = useState({});
  const [localEntry, setLocalEntry] = useState(entry);
  const [dirty, setDirty] = useState(defaultDirty);
  const [descFlipped, setDescFlipped] = useState(false);
  const [saving, setSaving] = useState(false);
  const [overrideEndDate, setOverrideEndDate] = useState(false);
  const [confirm, setConfirm] = useState(false);

  //only re-seed the working copy when a different entry loads - syncing on every change
  //to `entry` would overwrite whatever the user typed while a save was in flight
  useEffect(() => {
    if (entry) {
      setLocalEntry(entry);
      setOverrideEndDate(entry.startDate != entry.endDate);
    }
  }, [entry?.id]);

  //debounced on save function
  const lastAutoSaved = useRef(null);
  useDebouncedEffect(
    () => {
      if (!saving && dirty && onAutoSave) {
        //prevents autosaving forever on failure
        const snapshot = JSON.stringify({ ...localEntry, validationMessage: undefined });
        if (snapshot === lastAutoSaved.current) return;
        lastAutoSaved.current = snapshot;
        onAutoSave(localEntry);
      }
    },
    1000,
    [localEntry]
  );

  //return if there's no entry
  if (!entry?.id) return '';

  const updateLocalEntry = (updates) => {
    setDirty(true);
    setLocalEntry({ ...localEntry, ...updates });
  };

  const onSaveButton = () => {
    setSaving(true);
    if (onClearMessage) onClearMessage();
    const errs = onValidate(localEntry);
    if (errs) {
      setErrors(errs);
      if (errs.description) setDescFlipped(false);
      return false;
    } else {
      onSave(localEntry);
      return true;
    }
  };

  const onCancelButton = () => {
    if (dirty) setConfirm(true);
    else if (onCancel) onCancel(localEntry);
    // if (!dirty || confirm('Are you sure you want to cancel? All changes will be lost'))
    //   if (onCancel) {
    //     onCancel(localEntry);
    //   }
  };

  return (
    <Container
      component={Paper}
      style={{
        paddingBottom: 2,
        overflowX: 'hidden',
        height: 'fit-content',
      }}
    >
      <ConfirmDialog
        open={confirm}
        title="Are you sure you want to cancel?"
        message="All changes will be lost"
        onYes={() => {
          setConfirm(false);
          onCancel(localEntry);
        }}
        onNo={() => {
          setConfirm(false);
        }}
      />
      <form
        className={classes.root}
        style={{ height: 'fit-content' }}
        noValidate
        autoComplete="off"
      >
        <Grid container>
          <Grid item xs={12}>
            <TaskInput
              entry={localEntry}
              onChange={(t) => {
                if (t) {
                  updateLocalEntry({ ...t });
                } else {
                  updateLocalEntry({
                    companyName: null,
                    companyShortName: null,
                    incidentName: null,
                    incidentId: null,
                    taskId: null,
                    taskNumber: null,
                    taskName: null,
                  });
                }
              }}
              disabled={disabled}
              helperText={errors['task']}
              error={errors['task'] ? true : false}
            />
          </Grid>
          <Grid item xs={6}>
            <DateInput
              value={localEntry.startDate}
              label="Start"
              onChange={(d) => {
                if (overrideEndDate) {
                  updateLocalEntry({
                    startDate: d,
                    hours: dateUtil.calcHours(
                      d,
                      localEntry.startTime,
                      localEntry.endDate,
                      localEntry.endTime
                    ),
                  });
                } else {
                  updateLocalEntry({
                    startDate: d,
                    endDate: d,
                    hours: dateUtil.calcHours(
                      d,
                      localEntry.startTime,
                      d,
                      localEntry.endTime
                    ),
                  });
                }
              }}
              disabled={disabled}
              error={errors['startDate'] ? true : false}
              helperText={errors['startDate']}
              smallVersion={smallDates}
            />
          </Grid>
          <Grid item xs={6}>
            {overrideEndDate ? (
              <DateInput
                label="End"
                type="datetime-local"
                value={localEntry.endDate}
                error={errors['endDate'] ? true : false}
                helperText={errors['endDate']}
                onChange={(d) => {
                  updateLocalEntry({
                    endDate: d,
                    hours: dateUtil.calcHours(
                      localEntry.startDate,
                      localEntry.startTime,
                      d,
                      localEntry.endTime
                    ),
                  });
                }}
                disabled={disabled}
                smallVersion={smallDates}
              />
            ) : (
              <TextField
                margin="dense"
                label="End"
                disabled
                value={
                  dateUtil.stringToDateFormat(localEntry.endDate, 'MM/dd/yyyy') || ''
                }
                InputProps={{
                  style: { width: 'calc(100% - 14px)' },
                  endAdornment: (
                    <Create fontSize="small" onClick={() => setOverrideEndDate(true)} />
                  ),
                }}
              ></TextField>
            )}
          </Grid>

          <Grid item xs={6}>
            <TimeInput
              value={localEntry.startTime}
              onChange={(t) => {
                updateLocalEntry({
                  startTime: t,
                  hours: dateUtil.calcHours(
                    localEntry.startDate,
                    t,
                    localEntry.endDate,
                    localEntry.endTime
                  ),
                });
              }}
              disabled={disabled}
              error={errors['startTime'] ? true : false}
              helperText={errors['startTime']}
              smallVersion={smallDates}
            />
          </Grid>

          <Grid item xs={6}>
            <TimeInput
              type="datetime-local"
              value={localEntry.endTime}
              error={errors['endTime'] ? true : false}
              helperText={errors['endTime']}
              onChange={(t) => {
                updateLocalEntry({
                  endTime: t,
                  hours: dateUtil.calcHours(
                    localEntry.startDate,
                    localEntry.startTime,
                    localEntry.endDate,
                    t
                  ),
                });
              }}
              disabled={disabled}
              smallVersion={smallDates}
            />
          </Grid>

          <Grid item xs={6}>
            <HoursInput
              margin="dense"
              label="Billable hours"
              value={localEntry.hours || 0.0}
              setValue={(value) => updateLocalEntry({ hours: value })}
              disabled={disabled}
            />
          </Grid>
          <Grid item xs={6}>
            <HoursInput
              margin="dense"
              label="Mileage"
              value={localEntry.mileage || 0.0}
              setValue={(value) => updateLocalEntry({ mileage: value })}
              disabled={disabled}
            />
          </Grid>
          <Grid item xs={11} style={{ textAlign: 'right', maxHeight: 124 }}>
            <ReactCardFlip isFlipped={descFlipped} flipDirection="horizontal">
              <DescriptionInput
                label="description"
                value={localEntry.description}
                onChange={(val) => updateLocalEntry({ description: val })}
                disabled={disabled}
                helperText={errors['description']}
                error={errors['description'] ? true : false}
                inputProps={{ autoFocus: focus == 'description' }}
                setDirty={(value) => setDirty(value)}
              />
              <DescriptionInput
                label="tech note"
                value={localEntry.techNote}
                placeholder="Tech Note"
                onChange={(val) => updateLocalEntry({ techNote: val })}
                disabled={disabled}
                helperText={errors['techNote']}
                error={errors['techNote'] ? true : false}
                setDirty={(value) => setDirty(value)}
              />
            </ReactCardFlip>
          </Grid>
          <Grid item xs={1}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => setDescFlipped(!descFlipped)}
              size="small"
              style={{
                transform: 'rotate(90deg)',
                left: -35,
                top: 40,
              }}
            >
              DESC&nbsp;/&nbsp;NOTE
            </Button>
          </Grid>
          <Grid item xs={6}>
            <SwitchInput
              checked={localEntry.noCharge == 1}
              onChange={(e) => {
                updateLocalEntry({ noCharge: e.target.checked ? 1 : 0 });
              }}
              name="no-charge"
              color="primary"
              label="No charge"
              disabled={disabled}
            />
          </Grid>
          <Grid item xs={6}>
            <SwitchInput
              checked={localEntry.payBonus == 1}
              name="pay-bonus"
              color="primary"
              onChange={(e) => {
                updateLocalEntry({ payBonus: e.target.checked ? 1 : 0 });
              }}
              label="Pay bonus"
              disabled={disabled}
            />
          </Grid>
          <Grid item className={classes.actionBar} item xs={12}>
            <Grid container style={{ justifyContent: 'flex-end' }}>
              <Grid item xs={2}>
                <ApiMessage action={apiLabel} />
              </Grid>
              <Grid item className={classes.buttonContainer}>
                {!disabled && dirty && (
                  <Button onClick={onSaveButton} variant="contained" color="primary">
                    <Typography>{saveLabel}</Typography>
                  </Button>
                )}
              </Grid>
              <Grid item style={{ paddingLeft: 8 }} className={classes.buttonContainer}>
                <Button onClick={onCancelButton} variant="contained">
                  <Typography>
                    {dirty && cancelLabel}
                    {!dirty && closeLabel}
                  </Typography>
                </Button>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} style={{ textAlign: 'center', maxHeight: '30px' }}>
            <Typography style={{ paddingTop: 4, color: 'red' }}>
              {entry.validationMessage}
            </Typography>
          </Grid>
        </Grid>
      </form>
    </Container>
  );
};

export default EntryForm;
